import { prop, curry } from 'ramda';
import type { Client } from '@core/api/utils';
import { pipeData } from '@core/api/utils';
import { toApiLink } from '@core/external-links/models';
import { guessTimeZone } from '@core/time/time';
import { mapRideToClass } from '@engage/classes/mappers';
import type { Class } from '@engage/classes/models';
import type { PageNumber } from '@engage/pagination/PageNumber';
import { done } from '@engage/pagination/PageNumber';
import type { ApiWorkout } from '@engage/workouts/api';
import { toWorkout } from '@engage/workouts/api';
import type { Workout } from '@engage/workouts/models';

const NUM_WORKOUTS_PAGE = 10;

export const toDownloadWorkoutsLink = (userId: string) =>
  toApiLink(`/api/user/${userId}/workout_history_csv?timezone=${guessTimeZone()}`);

const toWorkoutsUrl = (
  userId: string,
  limit: number,
  page: number,
  fromDate?: string,
  toDate?: string,
) =>
  `/api/user/${userId}/workouts?joins=ride&limit=${limit}&page=${page}${
    !!fromDate && !!toDate ? `&from=${fromDate}&to=${toDate}` : ''
  }`;

export const fetchWorkouts = (
  api: Client,
  userId: string,
  page: number = 0,
  workoutsPerPage: number = NUM_WORKOUTS_PAGE,
  fromDate?: string,
  toDate?: string,
) =>
  api
    .get(toWorkoutsUrl(userId, workoutsPerPage, page, fromDate, toDate))
    .then(pipeData(toWorkoutHistoryData));

export const toWorkoutHistoryData = (
  response: ApiWorkoutHistory,
): WorkoutHistoryData => ({
  classes: toClasses(response.data),
  nextPage: toNextPage(response),
  pageCount: response.pageCount,
  total: response.total,
  workouts: toWorkouts(response.data),
  isPrivate: response.isPrivate,
});

export const toNextPage = (response: ApiNextPage): PageNumber =>
  response.showNext ? response.page + 1 : done;

export const toWorkouts = (apiWorkouts: ApiWorkout[]) => apiWorkouts.map(toWorkout);

export const toClasses = (apiWorkouts: ApiWorkout[]) =>
  apiWorkouts
    .map(curry(prop('ride')))
    .filter(Boolean)
    .map(mapRideToClass);

export type WorkoutHistoryData = {
  classes: Class[];
  nextPage: PageNumber;
  pageCount: number;
  total: number;
  workouts: Workout[];
  isPrivate?: boolean;
};

export type ApiNextPage = {
  showNext: boolean;
  page: number;
};

export type ApiWorkoutHistory = {
  data: ApiWorkout[];
  isPrivate?: boolean;
  pageCount: number;
  total: number;
} & ApiNextPage;
